/**
 * Sandbox chrome — the rail, the toolbar, and the frame drafts render inside.
 *
 * Drafts are discovered, not registered. Drop a `.tsx` file in `design/drafts/`
 * and it appears in the rail on the next HMR tick; delete it and it is gone.
 * There is no list to keep in sync, which is the whole point — the moment a
 * sandbox needs bookkeeping, people stop using it and edit the real app instead.
 */

import React, { Suspense, useEffect, useMemo, useState } from "react";
import { Link, Route, Routes, useLocation } from "react-router-dom";
import {
  AlertTriangle,
  Layers,
  Maximize2,
  Minimize2,
  Monitor,
  Moon,
  Smartphone,
  Sun,
  Tablet,
} from "lucide-react";

/* eager: false → each draft is its own lazy chunk, so one that throws on import
   does not take the rail down with it. */
const modules = import.meta.glob("./drafts/*.tsx");

type Draft = { slug: string; title: string; load: () => Promise<unknown> };

const drafts: Draft[] = Object.entries(modules)
  .map(([path, load]) => {
    const slug = path.replace("./drafts/", "").replace(/\.tsx$/, "");
    return {
      slug,
      title: slug
        .replace(/[-_]/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .trim(),
      load,
    };
  })
  .sort((a, b) => a.slug.localeCompare(b.slug));

const lazyFor = new Map<string, React.LazyExoticComponent<React.ComponentType>>();
for (const d of drafts) {
  lazyFor.set(
    d.slug,
    React.lazy(d.load as () => Promise<{ default: React.ComponentType }>)
  );
}

const WIDTHS = [
  { id: "full", label: "Full", px: null, Icon: Maximize2 },
  { id: "lg", label: "1280", px: 1280, Icon: Monitor },
  { id: "md", label: "1024", px: 1024, Icon: Monitor },
  { id: "sm", label: "768", px: 768, Icon: Tablet },
  { id: "xs", label: "390", px: 390, Icon: Smartphone },
] as const;

/* Choices survive a refresh. Reloading to check a change should not also cost
   you the dark-mode and width you had set up. */
function usePersisted<T>(key: string, initial: T) {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(`dsx:${key}`);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(`dsx:${key}`, JSON.stringify(value));
    } catch {
      /* private mode — the sandbox works fine without persistence */
    }
  }, [key, value]);
  return [value, setValue] as const;
}

class DraftBoundary extends React.Component<
  { slug: string; children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  /* Reset when you navigate to a different draft, otherwise one crash pins the
     error screen in place for every draft after it. */
  componentDidUpdate(prev: { slug: string }) {
    if (prev.slug !== this.props.slug && this.state.error) this.setState({ error: null });
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="dsx-error">
        <AlertTriangle size={20} />
        <div>
          <p className="dsx-error-title">This draft threw while rendering</p>
          <pre className="dsx-error-body">{this.state.error.message}</pre>
        </div>
      </div>
    );
  }
}

export const Sandbox: React.FC = () => {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "");

  const [dark, setDark] = usePersisted("dark", false);
  const [widthId, setWidthId] = usePersisted<string>("width", "full");
  const [sgCanvas, setSgCanvas] = usePersisted("sg", false);
  const [bare, setBare] = usePersisted("bare", false);

  /* The design system keys off `data-theme`; Tailwind's `darkMode: ["class"]`
     keys off `.dark`. Both are set, or half the page switches. */
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", dark);
    root.setAttribute("data-theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    if (!bare) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setBare(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [bare, setBare]);

  const width = useMemo(
    () => WIDTHS.find((w) => w.id === widthId) ?? WIDTHS[0],
    [widthId]
  );

  if (bare) {
    return (
      <div className="dsx-bare" {...(sgCanvas ? { "data-sg": "" } : {})}>
        <DraftCanvas slug={slug} widthPx={width.px} />
        <button className="dsx-restore" onClick={() => setBare(false)}>
          <Minimize2 size={13} /> Show chrome
        </button>
      </div>
    );
  }

  return (
    <div className="dsx-root">
      <aside className="dsx-rail">
        <div className="dsx-brand">
          <Layers size={15} />
          <span>Design sandbox</span>
        </div>

        <nav className="dsx-nav">
          {drafts.length === 0 && (
            <p className="dsx-empty">
              No drafts yet. Add a file to <code>design/drafts/</code>.
            </p>
          )}
          {drafts.map((d) => (
            <Link
              key={d.slug}
              to={`/${d.slug}`}
              className={`dsx-nav-item${slug === d.slug ? " is-active" : ""}`}
            >
              {d.title}
            </Link>
          ))}
        </nav>

        <p className="dsx-foot">
          Nothing here is bundled by <code>vite build</code>.
        </p>
      </aside>

      <div className="dsx-main">
        <header className="dsx-bar">
          <div className="dsx-group">
            {WIDTHS.map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setWidthId(id)}
                className={`dsx-chip${widthId === id ? " is-active" : ""}`}
                title={`Viewport ${label}`}
              >
                <Icon size={13} />
                {label}
              </button>
            ))}
          </div>

          <div className="dsx-group dsx-right">
            <button
              onClick={() => setSgCanvas(!sgCanvas)}
              className={`dsx-chip${sgCanvas ? " is-active" : ""}`}
              title="Apply the [data-sg] canvas scope from owen-theme.css"
            >
              data-sg
            </button>
            <button
              onClick={() => setDark(!dark)}
              className="dsx-chip"
              title={dark ? "Switch to light" : "Switch to dark"}
            >
              {dark ? <Moon size={13} /> : <Sun size={13} />}
              {dark ? "Dark" : "Light"}
            </button>
            <button
              onClick={() => setBare(true)}
              className="dsx-chip"
              title="Hide the sandbox chrome — Escape to bring it back"
            >
              <Maximize2 size={13} />
              Bare
            </button>
          </div>
        </header>

        <div className="dsx-stage" {...(sgCanvas ? { "data-sg": "" } : {})}>
          <DraftCanvas slug={slug} widthPx={width.px} />
        </div>
      </div>
    </div>
  );
};

const DraftCanvas: React.FC<{ slug: string; widthPx: number | null }> = ({
  slug,
  widthPx,
}) => (
  <div
    className="dsx-frame"
    style={widthPx ? { maxWidth: widthPx, margin: "0 auto" } : undefined}
  >
    <DraftBoundary slug={slug}>
      <Suspense fallback={<div className="dsx-loading">Loading draft…</div>}>
        <Routes>
          <Route
            path="/"
            element={
              <div className="dsx-loading">
                {drafts.length ? "Pick a draft from the left." : "Add a draft to begin."}
              </div>
            }
          />
          {drafts.map((d) => {
            const C = lazyFor.get(d.slug)!;
            return <Route key={d.slug} path={`/${d.slug}`} element={<C />} />;
          })}
          <Route path="*" element={<div className="dsx-loading">No draft at this path.</div>} />
        </Routes>
      </Suspense>
    </DraftBoundary>
  </div>
);
